import { useCallback } from 'react';

import { useFormContext } from '@/components/react-hook-form';
import { useDeathOrderAwareClient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/utilityHooks';
import {
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getDeathEventBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { useCurrentRecipients } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/hooks/formHooks';
import { PathsOf } from '@/types/subform';

import { ConfirmRemoveAllMenuItem } from './shared/ConfirmRemoveAllMenuItem';

interface RemoveAllRecipientsMenuItemProps {
  deathEvent: PrimaryClientDeathEvent;
}

export function RemoveAllRecipientsMenuItem({
  deathEvent,
}: RemoveAllRecipientsMenuItemProps) {
  const { setValue } = useFormContext<DispositiveProvisionsFormV2Shape>();

  const currentRecipients = useCurrentRecipients({ deathEvent });
  const dyingClient = useDeathOrderAwareClient({ deathEvent });

  const clientName = dyingClient?.displayGivenName || 'this client';

  const handleRemoveAllRecipients = useCallback(() => {
    const recipientsPath = `${getDeathEventBasePath(
      deathEvent
    )}.recipients` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>;

    setValue(recipientsPath, [], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [deathEvent, setValue]);

  if (!currentRecipients?.length) {
    return null;
  }

  return (
    <ConfirmRemoveAllMenuItem
      menuItemLabel="Remove all recipients in this death order"
      confirmHeading="Remove all recipients"
      confirmBody={
        <>All recipients after {clientName}&apos;s death will be removed.</>
      }
      onConfirm={handleRemoveAllRecipients}
    />
  );
}
